/**
 * Password policy validator — mirrors the backend rules in
 * backend/app/utils/password_validator.py so the live UI feedback and
 * the server-side check stay in sync.
 */

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export const MIN_LENGTH = 10;
export const MAX_LENGTH = 128;

const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`'"\\]/;

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${MIN_LENGTH} characters`,
    test: (p) => p.length >= MIN_LENGTH && p.length <= MAX_LENGTH,
  },
  {
    id: 'uppercase',
    label: 'At least one uppercase letter (A–Z)',
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: 'lowercase',
    label: 'At least one lowercase letter (a–z)',
    test: (p) => /[a-z]/.test(p),
  },
  {
    id: 'number',
    label: 'At least one number (0–9)',
    test: (p) => /[0-9]/.test(p),
  },
  {
    id: 'special',
    label: 'At least one special character (!@#$ etc.)',
    test: (p) => SPECIAL_CHARS_REGEX.test(p),
  },
];

export interface PasswordValidationResult {
  isValid: boolean;
  failedRuleIds: string[];
}

export function validatePassword(password: string): PasswordValidationResult {
  const failedRuleIds: string[] = [];
  for (const rule of PASSWORD_RULES) {
    if (!rule.test(password)) {
      failedRuleIds.push(rule.id);
    }
  }
  return { isValid: failedRuleIds.length === 0, failedRuleIds };
}
